import { MarketConfig } from './market-config';
import { MARKET_DE } from './market.de';
import { MARKET_IE } from './market.ie';
import { Preferences } from '@capacitor/preferences';
import { brandOfThisBuild } from '../brand/brand.token';
import { CHOSEN_MARKET_KEY, COUNTRY_OF_THIS_SESSION_KEY, detectMarketIdFromDevice, readSessionNote } from './native-markets';

/**
 * All markets this code base knows. Every build bundles every market (the configs are tiny objects) –
 * which one is USED is decided at runtime by findMarketForStart().
 */
export const ALL_MARKETS: MarketConfig[] = [
  MARKET_DE,
  MARKET_IE,   // Ireland (vehlogs) – the first English market.
];

export function findMarketById(marketId: string | null): MarketConfig | undefined {
  return ALL_MARKETS.find(market => market.id === marketId);
}

/**
 * The market (country setup) of this app start. Called by main.ts BEFORE Angular boots (plan R3 item 9).
 * The same for the native app and the websites. The country setup is fixed until the app loads again –
 * when another country must take effect, MarketSwitchService saves it and loads the app once more (D41).
 */
export async function findMarketForStart(): Promise<MarketConfig> {
  // 1. The app was just loaded again for another country (registration choice, login) – plan D41.
  //    The note of this session comes first, so that reload can never happen twice.
  const marketOfThisSession = findMarketById(readSessionNote(COUNTRY_OF_THIS_SESSION_KEY));
  if (marketOfThisSession !== undefined) {
    return marketOfThisSession;
  }

  // 2. The country saved on this device – by a registration or by the last login.
  const savedCountry = await Preferences.get({ key: CHOSEN_MARKET_KEY });
  const savedMarket = findMarketById(savedCountry.value);
  if (savedMarket !== undefined) {
    return savedMarket;
  }

  // 3. Local development: localStorage 'market' chooses, so every combination can be tested (unchanged).
  if (isLocalhost()) {
    const storedMarket = findMarketById(readStoredMarketIdForDevelopment());
    if (storedMarket !== undefined) {
      return storedMarket;
    }
  }

  // 4. Nothing saved: nobody was signed in on this device yet, and the app does not ask (plan D32).
  //    The country the phone suggests – the same one the page "Choose your country" pre-selects.
  const suggestedMarket = findMarketById(detectMarketIdFromDevice());
  if (suggestedMarket !== undefined) {
    return suggestedMarket;
  }

  // 5. No suggestion either: the usual country of the brand – KfzDoku → Germany, vehlogs → Ireland.
  const usualMarket = findMarketById(brandOfThisBuild().usualMarketId);
  if (usualMarket !== undefined) {
    return usualMarket;
  }
  return MARKET_DE;
}

function isLocalhost(): boolean {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

/** localStorage can throw (e.g. blocked site data) – then the default market is used. */
function readStoredMarketIdForDevelopment(): string | null {
  try {
    return window.localStorage.getItem('market');
  } catch {
    return null;
  }
}
